import { ISubQuoteProduct as ISubQuoteProductRead } from '../types';
import { ISubQuoteProduct as ISubQuoteProductWrite } from "./transformSubQuoteProducts";

export interface SubQuoteProductDiff {
  createPayload: ISubQuoteProductWrite[];
  updatePayload: Array<ISubQuoteProductWrite & { dogma_subquoteproductid: string }>;
  deleteIds: string[];
}

/**
 * Compares a set of new (transformed) sub quote products against the existing
 * records fetched from the API and returns three bucketed lists:
 *
 * - createPayload  — new items with no matching existing record
 * - updatePayload  — existing items whose tracked fields have changed
 * - deleteIds      — existing record IDs that are no longer present
 *
 * Matching is performed on the composite key: step + optionname + name.
 * This ensures options that share the same step/optionname (e.g. Width and
 * Height under "Dimension") are matched individually.
 */
export const diffSubQuoteProducts = (
  existingSubs: ISubQuoteProductRead[],
  newSubs: ISubQuoteProductWrite[],
): SubQuoteProductDiff => {
  const existingMap = new Map(
    existingSubs.map((sp) => [
      `${sp.dogma_step}__${sp.dogma_optionname}__${sp.dogma_name}`,
      sp,
    ]),
  );

  const matchedExistingIds = new Set<string>();
  const createPayload: ISubQuoteProductWrite[] = [];
  const updatePayload: Array<ISubQuoteProductWrite & { dogma_subquoteproductid: string }> = [];

  for (const newSp of newSubs) {
    const key = `${newSp.dogma_step}__${newSp.dogma_optionname}__${newSp.dogma_name}`;
    const existing = existingMap.get(key);

    if (existing) {
      matchedExistingIds.add(existing.dogma_subquoteproductid);

      const valueChanged =
        String(newSp.dogma_value ?? "") !== String(existing.dogma_value ?? "") ||
        newSp.dogma_amount !== existing.dogma_amount ||
        newSp.dogma_valuejson !== existing.dogma_valuejson;

      if (valueChanged) {
        updatePayload.push({ ...newSp, dogma_subquoteproductid: existing.dogma_subquoteproductid });
      }
    } else {
      createPayload.push(newSp);
    }
  }

  const deleteIds = existingSubs
    .filter((sp) => !matchedExistingIds.has(sp.dogma_subquoteproductid))
    .map((sp) => sp.dogma_subquoteproductid);

  return { createPayload, updatePayload, deleteIds };
};
